import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';

import { produce } from 'immer';
import { isEqual } from 'lodash';

import { checkItemMatch } from 'components/Filters/checkItemMatch';
import {
  DropdownState,
  FilterConfig,
  FiltersState,
  FilterState,
  FilterType,
  PillsState,
  RangeState,
  SearchState,
  SelectState,
  ToggleState,
} from 'components/Filters/types';

import { getActiveFilterPills } from './getActiveFilterPills';

export interface UseFiltersProps<T> {
  data?: T[];
  filterConfigs: FilterConfig<T>[];
  filtersState: FiltersState;
  requiredFiltersState?: FiltersState;
  keepFiltersOnceRequiredFiltersUpdate?: boolean;
  setFiltersState: (filtersState: FiltersState) => void | Dispatch<SetStateAction<FiltersState>>;
}

/**
 * Provides helper functions for Filter state, filters data
 * See [Confluence docs](https://confluence.lotlinx.com/display/LLD/Filters) for some definitions.
 */
export default function useFilters<T>({
  data,
  filterConfigs,
  filtersState,
  requiredFiltersState,
  keepFiltersOnceRequiredFiltersUpdate = true,
  setFiltersState,
}: UseFiltersProps<T>) {
  const setFiltersStateWithUpdater = setFiltersState as Dispatch<SetStateAction<FiltersState>>;

  useEffect(() => {
    if (requiredFiltersState && keepFiltersOnceRequiredFiltersUpdate) {
      setFiltersStateWithUpdater((currentFiltersState) =>
        produce(currentFiltersState, (draft) => {
          Object.entries(requiredFiltersState).forEach((filter) => {
            const [filterKey, filterState] = filter;
            const config = filterConfigs.find((c) => c.id === filterKey);
            if (config) {
              if (!draft[filterKey]) {
                draft[filterKey] = filterState;
              } else if (
                'search' in filterState &&
                filterState.search !== (draft[filterKey] as SearchState).search
              ) {
                draft[filterKey] = filterState;
              } else if ('min' in filterState || 'max' in filterState) {
                if (
                  filterState.min !== undefined &&
                  filterState.min >= 0 &&
                  filterState.max !== undefined &&
                  filterState.max >= 0
                ) {
                  (draft[filterKey] as RangeState).min = Math.max(
                    filterState.min,
                    (draft[filterKey] as RangeState)?.min ?? 0,
                  );
                  (draft[filterKey] as RangeState).max = Math.min(
                    filterState.max,
                    (draft[filterKey] as RangeState)?.max ?? Infinity,
                  );
                } else if (
                  filterState.min !== undefined &&
                  filterState.min >= 0 &&
                  (filterState.max === undefined || filterState.max < 0)
                ) {
                  (draft[filterKey] as RangeState).min = Math.max(
                    filterState.min,
                    (draft[filterKey] as RangeState)?.min ?? 0,
                  );
                  if (
                    ((draft[filterKey] as RangeState).min ?? 0) >
                    ((draft[filterKey] as RangeState).max ?? Infinity)
                  ) {
                    delete (draft[filterKey] as RangeState).max;
                  }
                } else if (
                  filterState.max !== undefined &&
                  filterState.max >= 0 &&
                  (filterState.min === undefined || filterState.min < 0)
                ) {
                  (draft[filterKey] as RangeState).max = Math.min(
                    filterState.max,
                    (draft[filterKey] as RangeState)?.max ?? Infinity,
                  );
                  if (
                    ((draft[filterKey] as RangeState).min ?? 0) >
                    ((draft[filterKey] as RangeState).max ?? Infinity)
                  ) {
                    delete (draft[filterKey] as RangeState).min;
                  }
                }
              } else if ('options' in filterState && filterState.options && draft[filterKey]) {
                filterState.options.forEach((option) => {
                  if (
                    !(
                      draft[filterKey] as SelectState | ToggleState | DropdownState
                    ).options?.includes(option)
                  ) {
                    (draft[filterKey] as SelectState | ToggleState | DropdownState).options?.push(
                      option,
                    );
                  }
                });
              }
            }
          });
          return draft;
        }),
      );
    } else if (requiredFiltersState && !keepFiltersOnceRequiredFiltersUpdate) {
      setFiltersStateWithUpdater((currentFiltersState) => {
        if (isEqual(requiredFiltersState, currentFiltersState)) {
          return currentFiltersState;
        }

        const newFiltersState: FiltersState = {};
        Object.entries(requiredFiltersState).forEach((filter) => {
          const [filterKey, filterState] = filter;
          const config = filterConfigs.find((c) => c.id === filterKey);
          if (config) {
            newFiltersState[filterKey] = filterState;
          }
        });
        return newFiltersState;
      });
    }
  }, [
    data,
    filterConfigs,
    requiredFiltersState,
    keepFiltersOnceRequiredFiltersUpdate,
    setFiltersStateWithUpdater,
  ]);

  const activeFilterPills: PillsState[] = getActiveFilterPills(filtersState, filterConfigs);

  const setFilterState = (id: string, state: FilterState) => {
    setFiltersState(
      produce(filtersState, (draft) => {
        draft[id] = state;
        return draft;
      }),
    );
  };

  const getFilterState = (id: string) => {
    return filtersState[id];
  };

  const clearFilter = (id: string) => {
    setFiltersState(
      produce(filtersState, (draft) => {
        if (draft[id]) {
          delete draft[id];
        }
        return draft;
      }),
    );
  };

  const clearSelectFilter = (id: string, option: string | number) => {
    const config = filterConfigs.find((config) => config.id === id);
    if (config?.type === FilterType.STATIC_SELECT || config?.type === FilterType.DYNAMIC_SELECT) {
      setFiltersState(
        produce(filtersState, (draft) => {
          if (draft[id]) {
            (draft[id] as SelectState).options = (draft[id] as SelectState).options?.filter(
              (item) => item !== option,
            );
          }
          if (!(draft[id] as SelectState).options?.length) {
            delete draft[id];
          }
          return draft;
        }),
      );
    }
  };

  const clearRangeFilter = (id: string, isMin: boolean) => {
    const config = filterConfigs.find((config) => config.id === id);
    if (config?.type === FilterType.RANGE) {
      setFiltersState(
        produce(filtersState, (draft) => {
          if (draft[id]) {
            isMin ? delete (draft[id] as RangeState).min : delete (draft[id] as RangeState).max;
            if ((draft[id] as RangeState).min === undefined) {
              delete (draft[id] as RangeState).min;
            }
            if ((draft[id] as RangeState).max === undefined) {
              delete (draft[id] as RangeState).max;
            }
          }
          if (Object.keys(draft[id]).length === 0) {
            delete draft[id];
          }
          return draft;
        }),
      );
    }
  };

  const clearAll = () => {
    setFiltersState({});
  };

  const filteredData = useMemo(
    () => data?.filter((item) => checkItemMatch(item, filterConfigs, filtersState)),
    [data, filterConfigs, filtersState],
  );
  return {
    filtersState,
    setFiltersState,
    requiredFiltersState,
    getFilterState,
    setFilterState,
    clearFilter,
    clearSelectFilter,
    clearRangeFilter,
    clearAll,
    filteredData,
    activeFilterPills,
  };
}
